import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { isEmpty } from 'lodash';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';

import { createMergeIntegration } from './actions/create-merge-integration';
import { deleteMergeIntegration } from './actions/delete-merge-integration';
import { loadMergeIntegration } from './actions/load-merge-integration';
import { updateMergeIntegration } from './actions/update-merge-integration';
import {
  mergeIntegrationForOrgIdSelector,
  mergeIntegrationsStateForOrgIdSelector,
} from './merge-integration-selectors';
import { openOrCloseGlobalDialog } from '../action/dialog/global-dialog';
import { loadHrisIntegrationResponse } from '../hris-integration-response/actions/load-hris-integration-response';
import { hrisIntegrationResponseForOrgIdSelector } from '../hris-integration-response/hris-integration-response-selectors';
import { userSelector } from '../reducers/current-user-selectors';
import { orgsForAccountId } from '../reducers/org-selectors';

import * as Api from '../api/api';
import { GLOBAL_DIALOGS } from '../dialogs/global-dialogs-config';
import { HrisIntegrationResponseDialog } from '../hris-integration-response/hris-integration-response-dialog';
import { UiDialog } from '../ui/ui-dialog';
import { SplitButtonRenderProps, UiSplitButton } from '../ui/ui-split-button';
import { multiOrgIntegrationConfigRoute } from '../web-routes';

import { MergeIntegrationDetailsDialogButton } from './merge-integration-details-dialog-button';
import { MergeIntegrationSettings } from './merge-integration-settings';

import { getHrisDisplayName } from './util/merge-display-utils';
import { MERGE_SYNC_STATUS } from './util/merge-sync-status';
import { MergeLink } from './widgets/merge-link';
import { isProd } from '../config/config';
import { confirm } from '../widgets/confirmation-dialog';

type PayrollActionOptions = {
  [key: string]: {
    itemName: string;
    handler: () => void;
    tooltip?: string;
  };
};

const mapStateToProps =
  ({ org }: InputProps) =>
  (state: any) => {
    const orgs = orgsForAccountId(state, org.accountId);
    const user = userSelector(state);

    return {
      hasMultipleOrgs: orgs ? orgs.length > 1 : false,
      hrisIntegrationResponse: hrisIntegrationResponseForOrgIdSelector(state, org.org_id),
      mergeIntegration: mergeIntegrationForOrgIdSelector(state, org.org_id),
      mergeIntegrationState: mergeIntegrationsStateForOrgIdSelector(state, org.org_id),
      user,
    };
  };

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>, { org }: InputProps) => ({
  actions: {
    loadHrisIntegrationResponse: () => dispatch(loadHrisIntegrationResponse(org.org_id)),
    loadMergeIntegration: () => dispatch(loadMergeIntegration(org.org_id)),
    updateMergeIntegration: (update: Partial<MergeIntegration>) =>
      dispatch(updateMergeIntegration(org.org_id, update)),
    deleteMergeIntegration: () => dispatch(deleteMergeIntegration(org.org_id)),
    createMergeIntegration: (publicToken: any) =>
      dispatch(createMergeIntegration(org.org_id, publicToken)),
    showGlobalErrorDialog: () =>
      dispatch(openOrCloseGlobalDialog(GLOBAL_DIALOGS.GLOBAL_ERROR.key, true)),
  },
});

type ButtonSizes = 'default' | 'xxs' | 'xs' | 'sm' | 'lg';
type InputProps = {
  className?: string;
  org: Org;
  size?: ButtonSizes;
  buttonIntegrationTextOverride?: string;
};

const MainButton = ({
  openClass,
  className,
  mergeIntegration,
  hrisIntegrationResponse,
  org,
  size,
}: SplitButtonRenderProps & {
  className?: string;
  mergeIntegration?: MergeIntegration;
  hrisIntegrationResponse?: HrisIntegrationResponse;
  org: Org;
  size?: ButtonSizes;
}) => (
  <MergeIntegrationDetailsDialogButton
    className={`${className} u-borderRadius-left-4 u-boxShadow-none u-border-right u-border-lightGrayDark ${openClass}`}
    org={org}
    mergeIntegration={mergeIntegration}
    hrisIntegrationResponse={hrisIntegrationResponse}
    buttonSize={size}
  />
);

const DropdownButtons = ({
  handleMenuItemClick,
  options,
}: {
  handleMenuItemClick: (action: string) => () => void;
  options: PayrollActionOptions;
}) => (
  <>
    {Object.entries(options).map(([action, { itemName, tooltip }], i) => (
      <Tooltip title={tooltip || ''} placement="right" key={action}>
        <MenuItem
          className={i === 0 ? 'u-borderRadius-top-none' : ''}
          onClick={handleMenuItemClick(action)}
        >
          {itemName}
        </MenuItem>
      </Tooltip>
    ))}
  </>
);

/**
 * Container that conditionally renders the "Manage HR/payroll" and "Sync with
 * HR/payroll" buttons depending on if there is an integration associated with
 * the organization. Neither button is rendered if the user is not an account admin, or
 * if the app is loading the integration.
 */
export const PayrollIntegrationButton = ({
  className,
  org,
  size,
  buttonIntegrationTextOverride,
}: InputProps) => {
  const {
    hasMultipleOrgs,
    mergeIntegration,
    mergeIntegrationState,
    hrisIntegrationResponse,
    user,
  } = useSelector(mapStateToProps({ org }), shallowEqual);

  const { actions } = mapDispatchToProps(useDispatch(), { org });

  const [isMergeLinkOpen, setIsMergeLinkOpen] = useState<boolean>(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState<boolean>(false);
  const [linkToken, setLinkToken] = useState<string | undefined>();

  const history = useHistory();

  const onReauthenticationSuccess = useCallback(
    (publicToken) => {
      try {
        actions.createMergeIntegration(publicToken);
      } catch {
        actions.showGlobalErrorDialog();
      }
    },
    [actions],
  );

  const hrisDisplayName = getHrisDisplayName(mergeIntegration, hrisIntegrationResponse);
  const isPaused = mergeIntegration?.syncStatus === MERGE_SYNC_STATUS.IMPORT_PAUSED;

  enum Action {
    START_NEW_IMPORT,
    RUN_IMPORT,
    MULTI_ORG_SYNC,
    UPDATE_SYNC_SETTINGS,
    REAUTHENTICATE,
    PAUSE,
    RESUME,
    DELETE,
  }

  // rendered on prod
  const handleStartNewImportClick = useCallback(async () => {
    if (!mergeIntegration) {
      return;
    }

    if (
      await confirm(
        `This will start a resync with ${hrisDisplayName}. Use it to refresh data when your directory in Team Engine is outdated. May take around 30 minutes to process.`,
      )
    ) {
      Api.post('/merge/force-resync', {
        body: JSON.stringify({ orgId: org.org_id }),
      });
    }
  }, [hrisDisplayName, mergeIntegration, org.org_id]);

  // rendered if admin
  const handleRunImportClick = useCallback(() => {
    Api.post('/merge/enqueue-import', {
      body: JSON.stringify({ orgId: org.org_id }),
    });
  }, [org.org_id]);

  // rendered if account has multiple orgs
  const handleConfigureMultipleOrganizationsClick = useCallback(() => {
    history.push(multiOrgIntegrationConfigRoute(org.org_id));
  }, [history, org.org_id]);

  const handleUpdateSyncSettingsClick = () => {
    setIsSettingsDialogOpen(true);
  };

  const handleUpdateSyncSettingsCloseClick = () => {
    setIsSettingsDialogOpen(false);
  };

  const handleReauthenticateClick = useCallback(async () => {
    setIsMergeLinkOpen(true);
    const response = await Api.post('/merge/link-token', {
      body: JSON.stringify({ orgId: org.org_id }),
    });
    if (!response.ok) {
      actions.showGlobalErrorDialog();
    }

    const data = await response.json();
    setLinkToken(data.linkToken);
  }, [actions, org.org_id]);

  const onMergeLinkExit = () => {
    setIsMergeLinkOpen(false);
  };

  // rendered if integration is paused
  const handlePauseClick = useCallback(async () => {
    if (!mergeIntegration) {
      return;
    }

    if (await confirm(`Are you sure you want to pause the integration with ${hrisDisplayName}?`)) {
      actions.updateMergeIntegration({
        syncStatus: MERGE_SYNC_STATUS.IMPORT_PAUSED,
      });
    }
  }, [actions, hrisDisplayName, mergeIntegration]);

  // rendered if integration is not prod
  const handleResumeClick = useCallback(() => {
    actions.updateMergeIntegration({
      syncStatus: MERGE_SYNC_STATUS.DONE,
    });
  }, [actions]);

  // rendered if admin
  const handleDeleteClick = useCallback(async () => {
    if (!mergeIntegration) {
      return;
    }

    if (
      await confirm(
        `Are you sure you want to remove the integration with ${hrisDisplayName}? This action cannot be undone. Contact support to temporarily pause your integration.`,
      )
    ) {
      actions.deleteMergeIntegration();
    }
  }, [actions, hrisDisplayName, mergeIntegration]);

  const options: PayrollActionOptions = useMemo(
    () => ({
      ...(isProd && !isPaused
        ? {
            [Action.START_NEW_IMPORT]: {
              itemName: 'Start New Import',
              handler: handleStartNewImportClick,
              tooltip:
                mergeIntegration?.syncStatus === 'SYNCING'
                  ? 'A sync is ongoing. Please wait until it is finished to attempt resyncing.'
                  : '',
            },
          }
        : {}),
      ...(user.isAdmin && !isPaused
        ? {
            [Action.RUN_IMPORT]: {
              itemName: 'Run Import',
              handler: handleRunImportClick,
              tooltip:
                'Makes the directory consistent with what we got from Merge after the last sync. Does not kick off a resync with Merge.dev.',
            },
          }
        : {}),
      ...(hasMultipleOrgs
        ? {
            [Action.MULTI_ORG_SYNC]: {
              itemName: 'Configure Multiple Organizations',
              handler: handleConfigureMultipleOrganizationsClick,
            },
          }
        : {}),
      [Action.UPDATE_SYNC_SETTINGS]: {
        itemName: 'Update Sync Settings',
        handler: handleUpdateSyncSettingsClick,
      },
      [Action.REAUTHENTICATE]: {
        itemName: 'Reauthenticate',
        handler: handleReauthenticateClick,
      },
      ...(!isPaused
        ? {
            [Action.PAUSE]: {
              itemName: 'Pause Sync',
              handler: handlePauseClick,
            },
          }
        : {
            [Action.RESUME]: {
              itemName: 'Resume Sync',
              handler: handleResumeClick,
            },
          }),
      ...(user.isAdmin
        ? {
            [Action.DELETE]: {
              itemName: 'Delete Integration',
              handler: handleDeleteClick,
            },
          }
        : {}),
    }),
    [
      Action,
      handleConfigureMultipleOrganizationsClick,
      handleDeleteClick,
      handlePauseClick,
      handleReauthenticateClick,
      handleResumeClick,
      handleRunImportClick,
      handleStartNewImportClick,
      hasMultipleOrgs,
      isPaused,
      mergeIntegration?.syncStatus,
      user.isAdmin,
    ],
  );

  const handleMenuItemClick = useCallback(
    (action: string) => () => {
      const handler = options[action]?.handler;
      if (!handler) {
        return;
      }

      handler();
    },
    [options],
  );

  useEffect(() => {
    actions.loadHrisIntegrationResponse();
    actions.loadMergeIntegration();
  });

  const Main = (splitButtonProps: SplitButtonRenderProps) =>
    useMemo(
      () => (
        <MainButton
          className={className}
          org={org}
          mergeIntegration={mergeIntegration}
          hrisIntegrationResponse={hrisIntegrationResponse}
          size={size}
          {...splitButtonProps}
        />
      ),
      [splitButtonProps],
    );

  const Dropdown = (splitButtonProps: SplitButtonRenderProps) =>
    useMemo(
      () => (
        <DropdownButtons
          handleMenuItemClick={handleMenuItemClick}
          options={options}
          {...splitButtonProps}
        />
      ),
      [splitButtonProps],
    );

  const renderMainButton = () => {
    const shouldRenderSplitButton =
      mergeIntegration?.orgId === org.org_id && (user.isAccountAdmin || user.isAdmin);
    return mergeIntegration && !isEmpty(mergeIntegration) ? (
      shouldRenderSplitButton ? (
        <UiSplitButton
          MainButton={Main}
          DropdownButtons={Dropdown}
          popperButtonVariant="secondary"
        />
      ) : (
        <MergeIntegrationDetailsDialogButton
          className={`${className} u-boxShadow u-borderRadius-4 u-border-right u-border-lightGrayDark`}
          org={org}
          mergeIntegration={mergeIntegration}
          hrisIntegrationResponse={hrisIntegrationResponse}
          buttonSize={size}
        />
      )
    ) : (
      <HrisIntegrationResponseDialog
        className={className}
        org={org}
        buttonSize={size}
        buttonIntegrationTextOverride={buttonIntegrationTextOverride}
      />
    );
  };

  return (
    <>
      {!mergeIntegrationState?.loading ? renderMainButton() : null}
      {isSettingsDialogOpen && (
        <UiDialog
          title="Update Sync Settings"
          size="sm"
          onClose={handleUpdateSyncSettingsCloseClick}
        >
          <UiDialog.Content>
            <MergeIntegrationSettings
              org={org}
              submitCallback={handleUpdateSyncSettingsCloseClick}
            />
          </UiDialog.Content>
        </UiDialog>
      )}
      {/* As always, some hackiness required when dealing with the Merge Link
      component. See the comment on the MergeLink wrapper below for background.
      Here, use the state variable isMergeLinkOpen to conditionally render the
      MergeLink wrapper so it only executes (thus opening the pop-up) immediately
      after the "Reauthenticate" button is clicked */}
      {isMergeLinkOpen && linkToken && (
        <MergeLink
          linkToken={linkToken}
          onSuccess={onReauthenticationSuccess}
          onExit={onMergeLinkExit}
        />
      )}
    </>
  );
};
